import { css } from "@emotion/css";
import { Button, Callout, noOp } from "@octopusdeploy/design-system-components";
import { space, text } from "@octopusdeploy/design-system-tokens";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { CreatePolicyResource, GitRef } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { useState } from "react";
import { useAnalyticPolicyCreatedDispatch } from "~/analytics/Analytics";
import type { TemplateWizardPageProps } from "~/areas/platformHub/TemplateWizardDialogLayout";
import TemplateWizardDialogLayout from "~/areas/platformHub/TemplateWizardDialogLayout";
import { AddPolicyStarterCard } from "~/areas/platformHub/policies/AddPolicyStarterCard";
import PolicySlugEditor from "~/areas/platformHub/policies/PolicySlugEditor";
import { PolicyStarterSummaryCard } from "~/areas/platformHub/policies/PolicyStarterSummaryCard";
import type { PolicyStarterResource } from "~/areas/platformHub/policies/policyStarters";
import { CustomDialog } from "~/components/Dialog/CustomDialog";
import { MediumDialogFrame } from "~/components/DialogLayout/Custom/index";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { Text } from "~/components/form/index";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
export enum CreatePolicySteps {
    "Details",
    "StarterSelection"
}
interface CreateMultiStepPolicyDialogProps {
    open: boolean;
    stepsToShow: CreatePolicySteps[];
    gitRef: GitRef;
    starters: PolicyStarterResource[];
}
export function CreateMultiStepPolicyDialog({ open, gitRef, stepsToShow, starters }: CreateMultiStepPolicyDialogProps) {
    const showDetails = stepsToShow.includes(CreatePolicySteps.Details);
    const showStarterSelection = stepsToShow.includes(CreatePolicySteps.StarterSelection);
    const [name, setName] = useState<string>("");
    const [slug, setSlug] = useState("");
    const [selectedStarter, setSelectedStarter] = useState<PolicyStarterResource | null>(null);
    const [showSelectionError, setShowSelectionError] = useState<boolean>(false);
    const navigation = useSpaceAwareNavigation();
    const isStarterPoliciesActive = useOctopusFeatureToggle("starter-policies", false);
    const dispatchPolicyCreated = useAnalyticPolicyCreatedDispatch();
    const [hasSaveBeenAttempted, setHasSaveBeenAttempted] = useState<boolean>(false);
    const createPolicyResource: CreatePolicyResource = {
        Name: "",
        GitRef: gitRef,
        Slug: "",
        Description: null,
        ScopeRego: selectedStarter?.ScopeRego.replace("${slug}", slug) ?? `package ${slug}\n\ndefault evaluate := true`,
        ConditionsRego: selectedStarter?.ConditionsRego.replace("${slug}", slug) ?? `package ${slug}\n\ndefault result := {"allowed": true}`,
        ViolationReason: null,
        ViolationAction: "block",
    };
    const { execute: createPolicy, error, isExecuting, } = useMutation({
        name: "Create Policy",
        action: async (repository) => {
            setHasSaveBeenAttempted(true);
            return await repository.Policy.create({ ...createPolicyResource, Name: name, Slug: slug, ChangeDescription: `Create policy '${name}'` });
        },
        afterComplete: async (_, output) => {
            if (isStarterPoliciesActive && selectedStarter) {
                dispatchPolicyCreated("Create Policy", { policyType: selectedStarter.Title });
            }
            navigation.navigate(links.editPolicyPage.generateUrl({ slug: output.Slug }, { gitRef: output.GitRef }));
        },
    });
    const pages: TemplateWizardPageProps[] = [];
    if (showStarterSelection) {
        pages.push({
            title: "Create a Policy",
            content: () => (<div>
                    {showSelectionError && !selectedStarter && (<div className={styles.callout}>
                            <Callout type="danger" title="Please select a starter policy to continue"/>
                        </div>)}
                    <div className={styles.description}>Create a policy to ensure compliance and governance, making it easier to enforce deployment controls at scale.</div>
                    <div className={styles.cardList}>
                        {starters.map((starter, index) => (<AddPolicyStarterCard key={index} title={starter.Title} description={starter.Description} cardType={starter.Type} selected={selectedStarter?.Title === starter.Title} onClick={() => {
                        setSelectedStarter(starter);
                        setShowSelectionError(false);
                    }}/>))}
                    </div>
                </div>),
            actionAvailable: true,
            allowNext: () => {
                if (!selectedStarter) {
                    setShowSelectionError(true);
                    return false;
                }
                return true;
            },
            additionalActions: [],
            statusBarLabel: "Select Starter Policy",
            hideAdditionalActionsOnSinglePage: true,
        });
    }
    if (showDetails) {
        pages.push({
            title: "Create Policy",
            content: ({ previous }) => (<div className={styles.content}>
                    {selectedStarter && (<PolicyStarterSummaryCard heading={selectedStarter.Title} subHeading={selectedStarter.Description} cardType={selectedStarter.Type} accessories={<Button label={"Change"} importance={"quiet"} onClick={() => previous()}/>}/>)}
                    <Text value={name} label="Name" onChange={(value) => {
                    setName(value);
                }}/>
                    <PolicySlugEditor label={"Slug"} value={slug} onChange={setSlug} policyName={name}/>
                </div>),
            actionAvailable: true,
            additionalActions: [],
            statusBarLabel: "Details",
            hideAdditionalActionsOnSinglePage: true,
            singlePageSaveLabel: "Create",
            singlePageSavingLabel: "Creating...",
        });
    }
    const shouldShowErrors = error && hasSaveBeenAttempted && !isExecuting;
    const errors = shouldShowErrors && error ? [error] : undefined;
    return (
    //TODO: Move this to Workflows, we want to do this by uplifting the multistep dialog to be a first class component in workflows.
    <CustomDialog open={open} close={noOp} render={(props) => (<TemplateWizardDialogLayout onSaveClick={async () => {
                await createPolicy();
                return true;
            }} busy={isExecuting} errors={shouldShowErrors ? errors : undefined} savePermission={{ permission: Permission.PlatformHubEdit }} frame={MediumDialogFrame} pages={pages}/>)}/>);
}
const styles = {
    content: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
    }),
    cardList: css({
        display: "flex",
        flexWrap: "wrap",
        gap: space[16],
        flexDirection: "row",
        padding: space[4],
    }),
    description: css({ font: text.body.regular.large, marginBottom: space[24] }),
    callout: css({
        margin: `0 0 ${space[32]} 0`,
    }),
};
